import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import { Box } from "@mui/joy";
import { Container, Grid, Link, Typography } from "@mui/material";
import SideBar from "../../components/Setting/Sidebar";
import { DeveloperHeader } from "../../components/DeveloperHeader";
import { useDeveloperContext } from "../../hook/useDeveloperContext";
import { useEffect, useState } from "react";
import { DeveloperInfo, LoginHistory } from "../../openApi/api";
import { developerApi, loginHistoryApi } from "../../openApi";
import { toast } from "react-toastify";
import { HTTP_RESPONSE_CODE } from "../../common/const";
import { getFormattedDateTime } from "../../common/helper";
import { LoginHistoryModal } from "../../components/LoginHistoryModal";
import { useRecoilState } from "recoil";
import { ThemeLight } from "../../state/ThemeLight";

const MyAccount = () => {
  const { userID, sessionKey, handleSessionKey } = useDeveloperContext();

  const [themeLight] = useRecoilState(ThemeLight);

  const [user, setUser] = useState<DeveloperInfo>();
  const [lastLoginHistory, setLastLoginHistory] = useState<LoginHistory[]>([]);

  const [showLoginHistory, setShowLoginHistory] = useState<boolean>(false);

  useEffect(() => {
    if (sessionKey !== undefined && sessionKey !== "" && userID !== 0) {
      developerApi
        .getInformation(userID.toString(), sessionKey)
        .then((res) => {
          setUser(res.data);
        })
        .catch((err) => {
          if (err.response) {
            if (
              err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
            ) {
              toast.error("Session key is expired", {
                theme: themeLight ? "light" : "dark",
              });
              handleSessionKey("");
            } else {
              toast.error(err.response.data.message, {
                theme: themeLight ? "light" : "dark",
              });
            }
          } else {
            toast.error(err.message, { theme: themeLight ? "light" : "dark" });
          }
        });

      loginHistoryApi
        .getLastLoggedInHistory(userID.toString(), sessionKey)
        .then((res) => {
          setLastLoginHistory(res.data.history ?? []);
        })
        .catch((err) => {
          if (err.response) {
            if (
              err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
            ) {
              toast.error("Session key is expired", {
                theme: themeLight ? "light" : "dark",
              });
              handleSessionKey("");
            } else {
              toast.error(err.response.data.message, {
                theme: themeLight ? "light" : "dark",
              });
            }
          } else {
            toast.error(err.message, { theme: themeLight ? "light" : "dark" });
          }
        });
    }
  }, [sessionKey, userID]);

  return (
    <>
      <DeveloperHeader title="Account Overview" />

      <Container className="container-xxl pb-16 mt-n14">
        <Grid container spacing={2}>
          <Grid item xl={3} lg={4}>
            <SideBar />
          </Grid>
          <Grid item xl={9} lg={8}>
            <Box className="card mb-4">
              <Box className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-header-title">Personal Info</h3>
              </Box>
              <Box className="card-body">
                <p>
                  Below are the username, email and overview information for
                  your account.
                </p>
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <AccountCircleOutlinedIcon className="text-icon" /> Your
                    username:
                  </Box>
                  <Box className="col-md-8">
                    <strong>{user?.username ?? ""}</strong>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <EmailOutlinedIcon className="text-icon" /> Your Email
                    Address:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center my-n1">
                      <strong>{user?.email ?? ""}</strong>
                      <Link
                        className="btn btn-sm btn-white"
                        style={{ minWidth: "6rem;" }}
                        href="/mysettings"
                      >
                        <CreateOutlinedIcon className="text-icon me-1" /> Edit
                      </Link>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <ExitToAppIcon className="text-icon" /> Last Login:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center my-n1">
                      <span>
                        <AccessTimeOutlinedIcon className="text-icon me-1" />
                        <span id="showUtcLocalDate">
                          {lastLoginHistory.length > 0
                            ? getFormattedDateTime(
                                lastLoginHistory[lastLoginHistory.length - 1]
                                  .loggedIn
                              )
                            : ""}{" "}
                        </span>
                      </span>
                      <Link
                        className="btn btn-sm btn-white"
                        style={{ minWidth: "6rem;" }}
                        href="#"
                        data-bs-toggle="modal"
                        data-bs-target="#showLoginHistory"
                        onClick={() => setShowLoginHistory(true)}
                      >
                        <RemoveRedEyeOutlinedIcon className="text-icon me-1" />{" "}
                        History
                      </Link>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className="card">
              <Box className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-header-title">Overview Usage</h3>
              </Box>
              <Box className="card-body">
                <p>
                  Usage of account features such as address watch list, address
                  name tags, and API keys.
                </p>
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <AccountBalanceWalletOutlinedIcon className="text-icon" />{" "}
                    Total UPC Balance (Watch List):
                  </Box>
                  <Box className="col-md-8">
                    0 UPC <span className="fs-sm text-muted">($0.00)</span>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <EmailOutlinedIcon className="text-icon" /> Email
                    Notification Limit:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center gap-2">
                      0 emails sent out
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                        100 daily limit
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <FavoriteBorderOutlinedIcon className="text-icon" /> Address
                    Watch List:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center gap-2">
                      <Link href="/myaddress">0 address alert(s)</Link>
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                        50 limit
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <InsertDriveFileOutlinedIcon className="text-icon" /> Txn
                    Private Notes
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center gap-2">
                      <Link href="/mynotes_tx">
                        0 transaction private note(s)
                      </Link>
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                        10,000 limit
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <StickyNote2OutlinedIcon className="text-icon" /> Address
                    Tags:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center gap-2">
                      <Link href="/mynotes_address">0 address tag(s)</Link>
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                        5,000 limit
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <StorageOutlinedIcon className="text-icon" /> API Key Usage:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center gap-2">
                      <Link href="/myapikey">0 active API(s)</Link>
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                        3 limit
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <VerifiedOutlinedIcon className="text-icon" /> Verified
                    Addresses
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center gap-2">
                      <Link href="/myverify_address">0 verified addresses</Link>
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium rounded-pill">
                        Unlimited
                      </span>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <LoginHistoryModal
        open={showLoginHistory}
        handleOpen={setShowLoginHistory}
        loginHistory={lastLoginHistory}
      />
    </>
  );
};

export default MyAccount;
